/* DynamicJEETest.jsx — Dynamic component for JEE Mains tests */
import React, { useState, useEffect, useRef } from "react";
import "./pyqjeetest.css";
import { auth, storage, db } from "../../../firebase";
import { ref as storageRef, listAll, getDownloadURL } from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { doc, getDoc, updateDoc, collection, addDoc } from "firebase/firestore";
import { getJEEQuestionType, getJEESubject } from '../../../data/freeJEETestData';

export default function DynamicJEETest({ testsMetadata }) {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { currentUser, loginWithGoogle } = useAuth();
  
  const testConfig = testsMetadata[testId];

  const [timeLeft, setTimeLeft] = useState(0);
  const [currentQues, setCurrentQues] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState([]);
  const [visited, setVisited] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [showFSPrompt, setShowFSPrompt] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [selectedResultQ, setSelectedResultQ] = useState(null); // floating circle click state
  const [resultActiveSubject, setResultActiveSubject] = useState('Physics');
  const [resultQLoading, setResultQLoading] = useState(false);
  const preloadResultImgRef = useRef(null);
  // Check live browser cache: complete+naturalWidth means image is decoded in memory right now
  const isImageCached = (url) => {
    if (!url) return true;
    const img = new Image();
    img.src = url;
    return img.complete && img.naturalWidth > 0;
  };

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track visited questions
  useEffect(() => {
    if (questions.length > 0 && questions[currentQues]) {
      const id = questions[currentQues].id;
      setVisited(prev => prev.includes(id) ? prev : [...prev, id]);
    }
  }, [currentQues, questions]);

  // Clear spinner: image questions → onLoad/onError on <img>; non-image questions → RAF after paint
  useEffect(() => {
    if (!questionLoading) return;
    const q = questions[currentQues];
    if (q && !q.img) {
      const id = requestAnimationFrame(() => setQuestionLoading(false));
      return () => cancelAnimationFrame(id);
    }
  }, [currentQues, questionLoading, questions]);

  const userName = currentUser?.displayName || currentUser?.email;

  useEffect(() => {
    if (testConfig) {
      setTimeLeft(testConfig.duration);
    }
  }, [testConfig]);

  // ✅ FIX: Use EXACT same logic as old working code
  useEffect(() => {
    if (!testConfig) {
      setLoadingQuestions(false);
      return;
    }

    let cancelled = false;
    async function fetchImages() {
      setLoadingQuestions(true);
      try {
        const folderRef = storageRef(storage, testConfig.storagePath + "/");
        const res = await listAll(folderRef);

        const items = await Promise.all(
          res.items.map(async (itemRef) => {
            const name = itemRef.name;
            const match = name.match(/(\d+)/);
            const id = match ? parseInt(match[1], 10) : null;
            let url = null;
            try {
              url = await getDownloadURL(storageRef(storage, `${testConfig.storagePath}/${name}`));
            } catch (err) {
              console.warn("Failed to get download URL for", name, err);
            }
            
            const answerKey = testConfig.answerKey;
            const ak = id && answerKey[id] !== undefined ? String(answerKey[id]).trim() : null;
            
            // ✅ FIX: Use pattern-based detection instead of answer value
            const questionType = getJEEQuestionType(id, testConfig.totalQuestions);
            
            return {
              id: id,
              question: "",
              img: url,
              type: questionType, // ✅ Now correctly detects based on position
              correctAnswer: ak || "",
            };
          })
        );

        const valid = items.filter((it) => it && it.id !== null);
        valid.sort((a, b) => a.id - b.id);

        if (!cancelled) {
          setQuestions(valid);
        }
      } catch (err) {
        console.error("Error listing storage folder:", err);
        if (!cancelled) setQuestions([]);
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    }

    fetchImages();
    return () => {
      cancelled = true;
    };
  }, [testConfig]);

  // Preload all question images into browser cache so palette switching is instant
  useEffect(() => {
    if (questions.length === 0) return;
    questions.forEach((q) => {
      if (q.img) {
        const img = new Image();
        img.src = q.img;
      }
    });
  }, [questions]);

  // Timer countdown
  useEffect(() => {
    if (showTest && !showResult && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showTest, showResult, timeLeft]);

  const formatTime = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  // ✅ ADD: Image zoom handlers
  const handleImageClick = (imageSrc) => {
    setZoomedImage(imageSrc);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseZoom = () => {
    setZoomedImage(null);
    document.body.style.overflow = 'auto';
  };

  // Hide navbar when test is active
  useEffect(() => {
    if (showTest || showResult) {
      document.body.classList.add('test-fullscreen-mode');
    } else {
      document.body.classList.remove('test-fullscreen-mode');
    }
    return () => {
      document.body.classList.remove('test-fullscreen-mode');
    };
  }, [showTest, showResult]);

  const handleStartTestClick = () => {
    if (currentUser) {
      setShowInstructions(true);
    } else {
      setShowLoginPopup(true);
    }
  };

  const handleLoginClick = () => {
    setShowLoginPopup(false);
    loginWithGoogle();
  };

  const fullscreenActive = useRef(false);

  const requestTestFullscreen = () => {
    fullscreenActive.current = true;
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
    if (req) req.call(el).catch(() => {});
  };
  const exitTestFullscreen = () => {
    fullscreenActive.current = false;
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
    if (exit && (document.fullscreenElement || document.webkitFullscreenElement)) exit.call(document).catch(() => {});
  };
  const handleCloseTest = () => {
    exitTestFullscreen();
    navigate(testConfig.backLink);
  };
  // Re-enter fullscreen if user presses Esc (unintentional exit)
  useEffect(() => {
    const onFSChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && fullscreenActive.current) {
        setShowFSPrompt(true);
      }
    };
    document.addEventListener('fullscreenchange', onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFSChange);
      document.removeEventListener('webkitfullscreenchange', onFSChange);
    };
  }, []);

  const handleProceedToTest = () => {
    if (agreeChecked) {
      requestTestFullscreen();
      setShowInstructions(false);
      setShowTest(true);
    }
  };

  const goToQuestion = (idx) => {
    const q = questions[idx];
    // Show spinner only if image is NOT currently in browser memory
    if (q && q.img && !isImageCached(q.img)) {
      setQuestionLoading(true);
    }
    setCurrentQues(idx);
  };

  const handleAnswerChange = (value) => {
    if (!questions || !questions[currentQues]) return;
    setAnswers({ ...answers, [questions[currentQues].id]: value });
  };

  const handleSaveNext = () => {
    if (currentQues < questions.length - 1) goToQuestion(currentQues + 1);
  };

  const handleClear = () => {
    if (!questions || !questions[currentQues]) return;
    const newAnswers = { ...answers };
    delete newAnswers[questions[currentQues].id];
    setAnswers(newAnswers);
  };

  const handleMarkReview = () => {
    if (!questions || !questions[currentQues]) return;
    if (!marked.includes(questions[currentQues].id))
      setMarked([...marked, questions[currentQues].id]);
    handleSaveNext();
  };

  const handleMarkReviewOnly = () => {
    if (!questions || !questions[currentQues]) return;
    // Clear any answer so state becomes orange (not evaluated)
    const newAnswers = { ...answers };
    delete newAnswers[questions[currentQues].id];
    setAnswers(newAnswers);
    if (!marked.includes(questions[currentQues].id))
      setMarked([...marked, questions[currentQues].id]);
    handleSaveNext();
  };

  const handleBack = () => {
    if (currentQues > 0) goToQuestion(currentQues - 1);
  };

  const handleNext = () => {
    if (currentQues < questions.length - 1) goToQuestion(currentQues + 1);
  };

  const selectResultQ = (globalIdx, imgUrl) => {
    if (globalIdx === null) { setSelectedResultQ(null); return; }
    if (preloadResultImgRef.current) {
      preloadResultImgRef.current.onload = null;
      preloadResultImgRef.current.onerror = null;
      preloadResultImgRef.current = null;
    }
    setSelectedResultQ(globalIdx);
    if (!imgUrl || isImageCached(imgUrl)) { setResultQLoading(false); return; }
    setResultQLoading(true);
    const img = new Image();
    preloadResultImgRef.current = img;
    img.onload = () => { preloadResultImgRef.current = null; setResultQLoading(false); };
    img.onerror = () => { preloadResultImgRef.current = null; setResultQLoading(false); };
    img.src = imgUrl;
  };

  const handleSubmit = () => {
    setShowSubmitConfirm(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowSubmitConfirm(false);
    setShowResult(true);
    setShowTest(false);
    setResultActiveSubject('Physics');
    selectResultQ(0, questions[0]?.img);

    const totalQuestions = questions.length;
    let attempted = 0, correct = 0, incorrect = 0, totalMarks = 0, negativeMarks = 0;

    // Subject-wise tracking
    const subjectStats = {
      Physics:     { score: 0, correct: 0, incorrect: 0, unattempted: 0 },
      Chemistry:   { score: 0, correct: 0, incorrect: 0, unattempted: 0 },
      Mathematics: { score: 0, correct: 0, incorrect: 0, unattempted: 0 },
    };

    const questionDetails = questions.map(q => {
      const userAnswer = answers[q.id] || null;
      const isCorrect = userAnswer !== null && String(userAnswer).trim() === String(q.correctAnswer).trim();
      const subject = getJEESubject(q.id, testConfig.totalQuestions);

      let marks = 0;
      if (userAnswer !== null) {
        attempted++;
        if (isCorrect) {
          correct++;
          totalMarks += 4;
          negativeMarks += 0;
          marks = 4;
          subjectStats[subject].correct++;
          subjectStats[subject].score += 4;
        } else {
          incorrect++;
          negativeMarks += 1;
          marks = -1;
          subjectStats[subject].incorrect++;
          subjectStats[subject].score -= 1;
        }
      } else {
        subjectStats[subject].unattempted++;
      }

      return {
        questionId: q.id,
        subject,
        userAnswer: userAnswer,
        correctAnswer: q.correctAnswer,
        status: userAnswer === null ? 'unattempted' : isCorrect ? 'correct' : 'incorrect',
        marks,
      };
    });

    const finalMarks = totalMarks - negativeMarks;
    const accuracy = attempted > 0 ? ((correct / attempted) * 100).toFixed(1) : '0.0';

    // Enrich subjectStats with accuracy
    const subjectScores = {};
    for (const [subj, stats] of Object.entries(subjectStats)) {
      const subjectAttempted = stats.correct + stats.incorrect;
      subjectScores[subj] = {
        ...stats,
        accuracy: subjectAttempted > 0 ? ((stats.correct / subjectAttempted) * 100).toFixed(1) : '0.0',
        total: questions.filter(q => getJEESubject(q.id, testConfig.totalQuestions) === subj).length * 4,
      };
    }

    let newAttempt = 1;

    if (currentUser) {
      const userRef = doc(db, "users", currentUser.uid);
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let testsTaken = userData.testsTaken || [];

          let existingTestIndex = testsTaken.findIndex((t) => t.testName === testConfig.testName);
          if (existingTestIndex !== -1) {
            newAttempt = (testsTaken[existingTestIndex].attempt || 0) + 1;
            testsTaken.splice(existingTestIndex, 1);
          }

          const resultData = {
            testId: testConfig.id,
            testName: testConfig.testName,
            testType: 'jee',
            score: finalMarks,
            total: totalQuestions * 4,
            attempted: attempted,
            correct: correct,
            incorrect: incorrect,
            unattempted: totalQuestions - attempted,
            accuracy: parseFloat(accuracy),
            totalQuestions: totalQuestions,
            date: new Date().toISOString(),
            attempt: newAttempt,
            storagePath: testConfig.storagePath,
            subjectScores,
            questionDetails: questionDetails,
          };

          // Save to scalable subcollection — failure here must NOT block the legacy update
          try {
            await addDoc(collection(db, "users", currentUser.uid, "testResults"), resultData);
          } catch (subErr) {
            console.error("Subcollection write failed:", subErr);
          }

          // Always keep legacy array for backward compatibility
          testsTaken.push(resultData);
          await updateDoc(userRef, { testsTaken: testsTaken });
        }
      } catch (err) {
        console.error("Error updating result:", err);
      }
    }
  };

  // Test not found
  if (!testConfig) {
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>Test Not Found</h2>
          <p>The requested test does not exist.</p>
          <button className="back-btn" onClick={() => navigate("/test-series/pyq/jeemains")}>
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  // Loading questions
  if (loadingQuestions) {
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>Loading test...</h2>
          <p>Fetching questions from Firebase Storage. Please wait.</p>
        </div>
      </div>
    );
  }

  // Login popup
  if (showLoginPopup) {
    return (
      <div className="modal-backdrop" style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 1000
      }}>
        <div className="login-modal" style={{
          backgroundColor: '#fff', padding: '30px', borderRadius: '8px',
          textAlign: 'center', maxWidth: '400px', width: '90%',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <h3 style={{ color: '#ef4444', marginBottom: '20px' }}>Login Required</h3>
          <p style={{ marginBottom: '30px' }}>
            Please log in with Google to start the test and save your progress.
          </p>
          <div className="button-row">
            <button
              onClick={handleLoginClick}
              className="start-test-btn"
              style={{
                backgroundColor: '#4285F4', color: 'white', padding: '10px 20px',
                border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px'
              }}
            >
              🔑 Login with Google
            </button>
            <button
              onClick={() => setShowLoginPopup(false)}
              className="back-btn"
              style={{
                backgroundColor: '#6b7280', color: 'white', padding: '10px 20px',
                border: 'none', borderRadius: '4px', cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Initial info page
  if (!showInstructions && !showTest && !showResult) {
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>{testConfig.testName}</h2>
          <p><strong>Subject:</strong> Physics, Chemistry, Mathematics</p>
          <p><strong>Duration:</strong> {testConfig.duration / 3600} Hours</p>
          <p><strong>Total Questions:</strong> {questions.length}</p>
          <p><strong>Type:</strong> Single Choice Questions & Numerical Questions</p>
          <p><strong>Marking:</strong> +4 for correct, -1 for incorrect, 0 for unattempted.</p>
          <div className="button-row">
            <button className="start-test-btn" onClick={handleStartTestClick}>Start Test</button>
            <button className="back-btn" onClick={() => navigate(testConfig.backLink)}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  // Instructions page
  if (showInstructions) {
    return (
      <div className="instructions-container">
        <h2>Please read the instructions carefully</h2>
        <div className="instruction-box">
          <h3>General Instructions:</h3>
          <p>Total duration of {testConfig.testName} is {testConfig.duration / 60} min.</p>
          <p>The clock will be set at the server. The countdown timer in the top right corner of screen will display the remaining time available for you to complete the examination. When the timer reaches zero, the examination will end by itself. You will not be required to end or submit your examination.</p>
          <p>The Questions Palette displayed on the right side of screen will show the status of each question using one of the following symbols:</p>
          <ul>
            <li>🔵 You have not visited the question yet.</li>
            <li>⚪ You have not answered the question.</li>
            <li>🟢 You have answered the question.</li>
            <li>🟡 You have NOT answered the question, but have marked the question for review.</li>
            <li>🟣 The question(s) "Answered and Marked for Review" will be considered for evaluation.</li>
          </ul>
          <h4>Navigating to a Question:</h4>
          <ul>
            <li>Click the question number in the palette to go directly to it.</li>
            <li>Click Save & Next to save your answer and move to the next question.</li>
            <li>Click Mark for Review & Next to save your answer, mark for review, and go to the next question.</li>
          </ul>
          <h4>Answering a Question:</h4>
          <ul>
            <li>For MCQ: Click the option button to select your answer.</li>
            <li>For Numerical: Type your answer in the input box.</li>
            <li>Always click Save & Next to save your answer.</li>
            <li>Use Mark for Review & Next to mark questions you want to revisit.</li>
          </ul>
          <p className="declaration">
            I have read and understood the instructions. All computer hardware allotted to me are in proper working condition.
            I declare that I am not in possession of any prohibited gadget or material.
          </p>
        </div>
        <div className="agree-section">
          <label>
            <input
              type="checkbox"
              checked={agreeChecked}
              onChange={(e) => setAgreeChecked(e.target.checked)}
            />{" "}
            I agree to the above instructions
          </label>
          <button
            className="proceed-btn"
            disabled={!agreeChecked}
            onClick={handleProceedToTest}
          >
            Start Test ➡
          </button>
        </div>
      </div>
    );
  }

  // Test interface
  if (showTest) {
    if (!questions || questions.length === 0) {
      return (
        <div className="mock-container">
          <div className="mock-header">
            <div className="user-info">
              <div className="user-icon">👤</div>
              <div>
                <p><strong>Candidate Name:</strong> {userName || "Guest"}</p>
                <p><strong>Test Name:</strong> {testConfig?.testName || "JEE Mains PYQ"}</p>
                <p><strong>Subject:</strong> {testConfig?.subject || "Physics, Chemistry, Mathematics"}</p>
              </div>
            </div>
            <div className="timer">
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>Remaining Time:</p>
              <span className="timer-value" style={{ 
                fontSize: '1.8rem', 
                fontWeight: 'bold', 
                color: timeLeft < 600 ? '#f44336' : '#2196F3' 
              }}>
                {formatTime(timeLeft)}
              </span>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                Total: {testConfig?.duration ? (testConfig.duration / 3600).toFixed(0) : '3'} Hours
              </p>
            </div>
          </div>
          <div className="mock-body">
            <div className="question-area">
              <h3>Loading questions...</h3>
            </div>
          </div>
        </div>
      );
    }

    const q = questions[currentQues];

    return (
      <div className="mock-container">
        {showFSPrompt && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: '20px'
          }}>
            <p style={{ color: '#fff', fontSize: '1.2rem', textAlign: 'center', margin: '0 20px' }}>
              ⚠️ Fullscreen is required during the test.<br/>Please return to fullscreen to continue.
            </p>
            <button
              onClick={() => { requestTestFullscreen(); setShowFSPrompt(false); }}
              style={{
                padding: '14px 36px', fontSize: '1rem', fontWeight: 600,
                background: '#2563eb', color: '#fff',
                border: 'none', borderRadius: '8px', cursor: 'pointer'
              }}
            >
              🔲 Return to Fullscreen
            </button>
          </div>
        )}
        {showSubmitConfirm && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9998,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#1e293b', borderRadius: '12px', padding: '32px 28px',
              maxWidth: '380px', width: '90%', textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}>
              <p style={{ color: '#f1f5f9', fontSize: '1.1rem', marginBottom: '8px', fontWeight: 600 }}>Submit Test?</p>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '24px' }}>Once submitted, you cannot change your answers.</p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={() => setShowSubmitConfirm(false)}
                  style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #475569', background: 'transparent', color: '#cbd5e1', cursor: 'pointer', fontWeight: 500 }}
                >Cancel</button>
                <button onClick={handleConfirmedSubmit}
                  style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                >Yes, Submit</button>
              </div>
            </div>
          </div>
        )}
        {zoomedImage && (
          <div className="image-modal-overlay">
            <div className="image-modal-backdrop" onClick={handleCloseZoom} />
            <div className="image-modal-content">
              <button className="image-modal-close" onClick={handleCloseZoom}>
                ✕
              </button>
              <img src={zoomedImage} alt="Question Full View" className="image-modal-img" />
            </div>
          </div>
        )}

        <div className="test-header-forced">
          <div className="header-left-info">
            <span className="user-emoji">👤</span>
            <div className="user-details">
              <div className="info-line">
                <span className="label-bold">Candidate Name:</span> 
                <span className="value-text">{userName || "Guest"}</span>
              </div>
              <div className="info-line">
                <span className="label-bold">Test Name:</span> 
                <span className="value-text">{testConfig?.testName || "JEE Mains Test"}</span>
              </div>
              <div className="info-line">
                <span className="label-bold">Subject:</span> 
                <span className="value-text">{testConfig?.subject || "Physics, Chemistry, Mathematics"}</span>
              </div>
            </div>
          </div>
          
          <div className="header-timer-section">
            <div className="timer-label">Remaining Time:</div>
            <div className="timer-big-value" style={{ color: timeLeft < 600 ? '#f44336' : '#2196F3' }}>
              {formatTime(timeLeft)}
            </div>
            <div className="timer-total">Total: {testConfig?.duration ? (testConfig.duration / 3600).toFixed(0) : '3'} Hours</div>
          </div>
        </div>

<div className="mock-body">
          <div className="question-area">
            {questionLoading && (
              <div className="question-loading-overlay">
                <div className="question-spinner"></div>
                <p className="question-loading-text">Loading question...</p>
              </div>
            )}
            <div
              className="question-clickable"
              onClick={() => q.img && handleImageClick(q.img)}
              style={q.img ? { cursor: 'zoom-in' } : {}}
            >
              <h4>Question {q.id}</h4>
              {q.img && (
                <div className="image-section">
                  <div className="question-image-wrapper">
                    <img
                      src={q.img}
                      alt={`Q${q.id}`}
                      onLoad={() => setQuestionLoading(false)}
                      onError={() => setQuestionLoading(false)}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {q.type === "mcq" && (
              isDesktop ? (
                <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Options:</span>
                  {[1, 2, 3, 4].map((n) => {
                    const val = String(n);
                    return (
                      <label key={n} className="option">
                        <input
                          type="radio"
                          name={`q${q.id}`}
                          value={val}
                          checked={answers[q.id] === val}
                          onChange={(e) => handleAnswerChange(e.target.value)}
                        />
                        {`${val})`}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div style={{ marginTop: '20px' }}>
                  <span style={{ fontWeight: '600', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>Options:</span>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    {[1, 2, 3, 4].map((n) => {
                      const val = String(n);
                      return (
                        <label key={n} className="option">
                          <input
                            type="radio"
                            name={`q${q.id}`}
                            value={val}
                            checked={answers[q.id] === val}
                            onChange={(e) => handleAnswerChange(e.target.value)}
                          />
                          {`${val})`}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )
            )}
            
            {q.type === "numerical" && (
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="numeric-input"
                value={answers[q.id] || ""}
                onChange={(e) => handleAnswerChange(e.target.value)}
                placeholder="Enter non-negative integer"
              />
            )}
            
            <div className="button-row">
              <button className="save-btn" onClick={handleSaveNext}>Save & Next</button>
              <button className="clear-btn" onClick={handleClear}>Clear Response</button>
              <button className="mark-only-btn" onClick={handleMarkReviewOnly}>Mark for Review</button>
              <button className="mark-btn" onClick={handleMarkReview}>Answered &amp; Mark for Review</button>
            </div>
            <div className="button-row" style={{ marginTop: "15px" }}>
              <button className="nav-btn" onClick={handleBack}>Back</button>
              <button className="nav-btn" onClick={handleNext}>Next</button>
              <button className="submit-btn" onClick={handleSubmit}>Submit Test</button>
              <button className="close-btn1" onClick={handleCloseTest}>❌ Close Test</button>
            </div>
          </div>
          
          <div className="palette-col">
          <div className="question-palette" style={{ display: 'flex', flexDirection: 'column' }}>
            <h4 style={{ margin: '0 0 15px 0', flexShrink: 0 }}>Question Palette</h4>
            <div className="palette-grid" style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '15px', flex: 1, scrollbarWidth: 'thin' }}>
              {questions.map((qq, i) => {
                const isAnswered = answers[qq.id] !== undefined && answers[qq.id] !== null && answers[qq.id] !== "";
                const isMarked = marked.includes(qq.id);
                const isVisited = visited.includes(qq.id);
                const isCurrent = currentQues === i;

                let className = "palette-btn";
                if (isAnswered && isMarked) className += " answered-marked";
                else if (isAnswered) className += " answered";
                else if (isMarked) className += " marked";
                else if (!isVisited) className += " not-visited";
                if (isCurrent) className += " active";

                return (
                  <button
                    key={qq.id}
                    className={className}
                    onClick={() => goToQuestion(i)}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '8px',
              fontSize: '0.75rem',
              borderTop: '2px solid #eee',
              paddingTop: '12px',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: '#1976D2', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Not Visited</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: 'white', border: '2px solid #ccc', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Not Answered</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: '#2E7D32', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Answered</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: '#E65100', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Marked for Review (Not Eval.)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: '#6A1B9A', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Answered &amp; Marked (Eval.)</span>
              </div>
            </div>
          </div>
          </div>{/* end palette-col */}
        </div>
      </div>
    );
  }

  // Result page
  if (showResult) {
    const totalQuestions = questions.length;
    let attempted = 0, correct = 0, incorrect = 0;
    const subjectStats = {
      Physics:     { correct: 0, incorrect: 0, unattempted: 0, score: 0 },
      Chemistry:   { correct: 0, incorrect: 0, unattempted: 0, score: 0 },
      Mathematics: { correct: 0, incorrect: 0, unattempted: 0, score: 0 },
    };

    questions.forEach((q) => {
      const ans = answers[q.id];
      const subject = getJEESubject(q.id, testConfig.totalQuestions);
      if (ans) {
        attempted++;
        if (String(ans).trim() === String(q.correctAnswer).trim()) {
          correct++;
          subjectStats[subject].correct++;
          subjectStats[subject].score += 4;
        } else {
          incorrect++;
          subjectStats[subject].incorrect++;
          subjectStats[subject].score -= 1;
        }
      } else {
        subjectStats[subject].unattempted++;
      }
    });

    const finalMarks = (correct * 4) - (incorrect * 1);
    const accuracy = attempted > 0 ? ((correct / attempted) * 100).toFixed(1) : '0.0';
    const unattempted = totalQuestions - attempted;

    const SUBJECTS_JEE = ['Physics', 'Chemistry', 'Mathematics'];

    // Selected question for floating circle detail
    const selQ = selectedResultQ !== null ? questions[selectedResultQ] : null;

    return (
      <div className="mock-container">
        {showFSPrompt && (
          <div style={{ position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.92)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'20px' }}>
            <p style={{ color:'#fff',fontSize:'1.2rem',textAlign:'center',margin:'0 20px' }}>⚠️ Fullscreen is required during the test.</p>
            <button onClick={() => { requestTestFullscreen(); setShowFSPrompt(false); }} style={{ padding:'14px 36px',fontSize:'1rem',fontWeight:600,background:'#2563eb',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer' }}>🔲 Return to Fullscreen</button>
          </div>
        )}

        <div className="result-header">
          <h2>📊 Test Result &amp; Analysis</h2>
          <button className="result-back-btn" onClick={handleCloseTest}>⬅️ Back to Test Series</button>
        </div>

        <div className="result-page-body">

          {/* ══════════════════════════════════════════════
              SECTION 1: RESULT SUMMARY
          ══════════════════════════════════════════════ */}
          <section className="result-section">
            <h3 className="result-section-title">📋 Result Summary</h3>
            <p className="result-test-name">{testConfig.testName}</p>
            <p className="result-candidate">👤 {userName}</p>

            {/* Score & Accuracy Cards */}
            <div className="result-top-cards">
              <div className="rtop-card score-card">
                <div className="rtop-label">SCORE</div>
                <div className="rtop-value">{finalMarks}<span className="rtop-max">/{totalQuestions * 4}</span></div>
              </div>
              <div className="rtop-card accuracy-card">
                <div className="rtop-label">ACCURACY</div>
                <div className="rtop-value">{accuracy}<span className="rtop-max">%</span></div>
              </div>
              <div className="rtop-card attempted-card">
                <div className="rtop-label">ATTEMPTED</div>
                <div className="rtop-value">{attempted}<span className="rtop-max">/{totalQuestions}</span></div>
              </div>
            </div>

            {/* Your Progress */}
            <div className="your-progress-box">
              <h4 className="progress-heading">Your Progress</h4>
              <div className="progress-row">
                <div className="prog-item correct-prog">
                  <span className="prog-icon">✅</span>
                  <span className="prog-label">Correct</span>
                  <span className="prog-count">{correct}/{totalQuestions}</span>
                  <span className="prog-marks">+{correct * 4}</span>
                </div>
                <div className="prog-item incorrect-prog">
                  <span className="prog-icon">❌</span>
                  <span className="prog-label">Incorrect</span>
                  <span className="prog-count">{incorrect}/{totalQuestions}</span>
                  <span className="prog-marks">-{incorrect}</span>
                </div>
                <div className="prog-item skipped-prog">
                  <span className="prog-icon">⏭</span>
                  <span className="prog-label">Skipped</span>
                  <span className="prog-count">{unattempted}/{totalQuestions}</span>
                  <span className="prog-marks">0</span>
                </div>
              </div>
            </div>

            {/* Subject-wise Performance Table */}
            <div className="subject-perf-table-wrapper">
              <h4 className="subject-perf-heading">Section-wise Performance</h4>
              <div className="subject-perf-table">
                <div className="spt-header">
                  <span>Section</span><span>Score</span><span>Correct</span><span>Incorrect</span><span>Skipped</span><span>Accuracy</span>
                </div>
                {SUBJECTS_JEE.map(subj => {
                  const s = subjectStats[subj];
                  const subjectAttempted = s.correct + s.incorrect;
                  const subjectTotal = questions.filter(q => getJEESubject(q.id, testConfig.totalQuestions) === subj).length;
                  const subjectMaxMarks = subjectTotal * 4;
                  const subjectAccuracy = subjectAttempted > 0 ? ((s.correct / subjectAttempted) * 100).toFixed(1) : '0.0';
                  return (
                    <div key={subj} className="spt-row">
                      <span className={`spt-subject spt-${subj.toLowerCase().substring(0,4)}`}>{subj}</span>
                      <span>{s.score}/{subjectMaxMarks}</span>
                      <span className="spt-correct">{s.correct}</span>
                      <span className="spt-incorrect">{s.incorrect}</span>
                      <span className="spt-skipped">{s.unattempted}</span>
                      <span>{subjectAccuracy}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              SECTION 2: QUESTION-WISE ANALYSIS
          ══════════════════════════════════════════════ */}
          <section className="result-section">
            <h3 className="result-section-title">🔍 Question-wise Analysis</h3>
            <p className="floating-q-hint">Tap a number to view question details · 🟢 Correct &nbsp; 🔴 Incorrect &nbsp; ⚪ Skipped</p>

            <div className="floating-circles-row" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
              {questions.map((q) => {
                const globalIdx = questions.indexOf(q);
                const ans = answers[q.id];
                const isCorrect = ans && String(ans).trim() === String(q.correctAnswer).trim();
                const statusClass = !ans ? 'fcircle-skip' : isCorrect ? 'fcircle-correct' : 'fcircle-wrong';
                const isSelected = selectedResultQ === globalIdx;
                return (
                  <button
                    key={q.id}
                    className={`floating-circle ${statusClass} ${isSelected ? 'fcircle-selected' : ''}`}
                    onClick={() => selectResultQ(isSelected ? null : globalIdx, q.img)}
                    title={`Q${q.id}: ${!ans ? 'Skipped' : isCorrect ? 'Correct' : 'Incorrect'}`}
                  >
                    {q.id}
                  </button>
                );
              })}
            </div>

            {/* Question Detail Panel */}
            {selQ && (
              <div key={selectedResultQ} className={`result-q-detail-panel ${answers[selQ.id] && String(answers[selQ.id]).trim() === String(selQ.correctAnswer).trim() ? 'panel-correct' : answers[selQ.id] ? 'panel-wrong' : 'panel-skip'}`}>
                <div className="rqdp-header">
                  <span className="rqdp-qnum">Q{selQ.id} — {getJEESubject(selQ.id, testConfig.totalQuestions)}</span>
                  <button className="rqdp-close" onClick={() => setSelectedResultQ(null)}>✕</button>
                </div>
                {resultQLoading ? (
                  <div className="rqdp-spinner-wrap"><div className="rqdp-spinner"></div></div>
                ) : (
                  <>
                    {selQ.img && (
                      <div className="rqdp-img-wrapper" onClick={() => handleImageClick(selQ.img)} style={{ cursor: 'zoom-in' }}>
                        <img src={selQ.img} alt={`Q${selQ.id}`} className="rqdp-img" />
                        <span className="rqdp-zoom-hint">🔍 Tap to zoom</span>
                      </div>
                    )}
                    <div className="rqdp-answers">
                      <div className="rqdp-ans-row">
                        <span className="rqdp-ans-label">Your Answer:</span>
                        <span className={`rqdp-ans-val ${answers[selQ.id] ? (String(answers[selQ.id]).trim() === String(selQ.correctAnswer).trim() ? 'ans-correct' : 'ans-wrong') : 'ans-skip'}`}>
                          {answers[selQ.id] || 'Not Answered'}
                        </span>
                      </div>
                      <div className="rqdp-ans-row">
                        <span className="rqdp-ans-label">Correct Answer:</span>
                        <span className="rqdp-ans-val ans-correct">{selQ.correctAnswer || '—'}</span>
                      </div>
                      <div className="rqdp-ans-row">
                        <span className="rqdp-ans-label">Marks:</span>
                        <span className="rqdp-ans-val">
                          {!answers[selQ.id] ? '0' : String(answers[selQ.id]).trim() === String(selQ.correctAnswer).trim() ? '+4' : '-1'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Zoom modal */}
            {zoomedImage && (
              <div className="image-modal-overlay">
                <div className="image-modal-backdrop" onClick={handleCloseZoom} />
                <div className="image-modal-content">
                  <button className="image-modal-close" onClick={handleCloseZoom}>✕</button>
                  <img src={zoomedImage} alt="Full View" className="image-modal-img" />
                </div>
              </div>
            )}
          </section>

        </div>
      </div>
    );
  }

  return null;
}